import { useState, useRef, useEffect, useCallback } from 'react';

const BASELINE = 10000;
const MAX_LEAD_IN = 15;

function getSyncAnchor(videoData) {
  if (videoData == null) return null;
  if (videoData.greenFlag != null && videoData.greenFlag > 0) return videoData.greenFlag;
  if (videoData.crossStart != null && videoData.crossStart > 0) return videoData.crossStart;
  return null;
}

function computeLeadIn(slots) {
  const anchors = slots.map(getSyncAnchor).filter((a) => a != null && a > 0);
  if (anchors.length === 0) return 0;
  return Math.min(MAX_LEAD_IN, ...anchors);
}

function calcVideoTime(videoData, timelineTime, leadIn) {
  const anchor = getSyncAnchor(videoData);
  if (anchor == null) return 0;
  return Math.max(0, anchor - leadIn + (timelineTime - BASELINE));
}

export function useBroadcastSync(initialVideoDataArray = []) {
  const [slots, setSlots] = useState(() => {
    const arr = [...initialVideoDataArray].slice(0, 3);
    while (arr.length < 3) arr.push(null);
    return arr;
  });

  const [primaryIndex, setPrimaryIndex] = useState(0);

  // UI state — updated from the RAF loop
  const [timeline, setTimeline] = useState({
    currentTime: BASELINE,
    isPlaying: false,
    playbackRate: 1,
  });

  // Refs for the hot path — RAF reads these directly to avoid stale closures
  const isPlayingRef = useRef(false);
  const playbackRateRef = useRef(1);
  const currentTimeRef = useRef(BASELINE);
  const lastTickRef = useRef(null);
  const rafRef = useRef(null);

  const playerRefs = useRef([null, null, null]);
  const slotsRef = useRef(slots);
  useEffect(() => { slotsRef.current = slots; }, [slots]);

  // Single RAF loop — starts once, never restarts.
  // Reads isPlayingRef so it responds to play/pause without restarting.
  useEffect(() => {
    const tick = (now) => {
      if (isPlayingRef.current) {
        const delta = lastTickRef.current != null
          ? ((now - lastTickRef.current) / 1000) * playbackRateRef.current
          : 0;
        lastTickRef.current = now;
        currentTimeRef.current += delta;
        setTimeline((prev) => ({ ...prev, currentTime: currentTimeRef.current }));
      } else {
        lastTickRef.current = null;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // empty deps — runs once

  const leadIn = computeLeadIn(slots);

  // ── Seek helpers ────────────────────────────────────────────────────────────

  const seekAllPlayers = useCallback((timelineTime) => {
    const li = computeLeadIn(slotsRef.current);
    slotsRef.current.forEach((videoData, i) => {
      const ref = playerRefs.current[i];
      if (ref && videoData) {
        const vt = calcVideoTime(videoData, timelineTime, li);
        ref.seekTo(vt, 'seconds');
      }
    });
  }, []);

  // Re-seek when greenFlag values change (after GreenFlagWidget saves)
  const prevSlotsRef = useRef(slots);
  useEffect(() => {
    if (currentTimeRef.current === BASELINE) return;
    if (prevSlotsRef.current === slots) return;
    prevSlotsRef.current = slots;
    const t = setTimeout(() => seekAllPlayers(currentTimeRef.current), 150);
    return () => clearTimeout(t);
  }, [slots, seekAllPlayers]);

  // ── Controls ────────────────────────────────────────────────────────────────

  const play = useCallback(() => {
    seekAllPlayers(currentTimeRef.current);
    lastTickRef.current = null;
    isPlayingRef.current = true;
    setTimeline((prev) => ({ ...prev, isPlaying: true }));
  }, [seekAllPlayers]);

  const pause = useCallback(() => {
    isPlayingRef.current = false;
    setTimeline((prev) => ({ ...prev, isPlaying: false }));
  }, []);

  const scrub = useCallback((newTimelineTime) => {
    currentTimeRef.current = newTimelineTime;
    isPlayingRef.current = false;
    setTimeline((prev) => ({ ...prev, currentTime: newTimelineTime, isPlaying: false }));
    seekAllPlayers(newTimelineTime);
  }, [seekAllPlayers]);

  const skip = useCallback((seconds) => {
    const newTime = currentTimeRef.current + seconds;
    currentTimeRef.current = newTime;
    setTimeline((prev) => ({ ...prev, currentTime: newTime }));
    seekAllPlayers(newTime);
  }, [seekAllPlayers]);

  // ── Slot management ─────────────────────────────────────────────────────────

  const promote = useCallback((index) => {
    setPrimaryIndex(index);
  }, []);

  const updateVideoData = useCallback((updatedVideoData) => {
    setSlots((prev) =>
      prev.map((v) => (v?.id === updatedVideoData.id ? { ...v, ...updatedVideoData } : v))
    );
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const needsGreenFlag = useCallback((videoData) => {
    return videoData != null && getSyncAnchor(videoData) == null;
  }, []);

  return {
    slots,
    primaryIndex,
    timeline,
    playerRefs,
    play,
    pause,
    scrub,
    skip,
    promote,
    updateVideoData,
    needsGreenFlag,
    calcVideoTime: (videoData) => calcVideoTime(videoData, currentTimeRef.current, leadIn),
    BASELINE,
    leadIn,
  };
}

export default useBroadcastSync;
